import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import { getSkillTitle, type SkillShort } from '~/shared/data-contract/skill';
import { DropdownMenuItem, DropdownMenuLabel } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconCircleCheckLight, IconSearch } from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Spinner } from '~/ui/core/spinner/Spinner';

import { useSnackbars } from '../../../app/error-handling/SnackBars';
import { useRelatedSkills } from '../../api/use-related-skills';
import { useSkillsSearch } from '../../api/use-skills-search';
import { SkillNotSyncedWarning } from '../../components/SkillNotSyncedWarning';
import { useTrackSearch } from '../../hooks/analytics/use-track-search';
import type { ProfileForm } from '../types';

type UseSkillSearchParams = {
    readonly architectureId?: string;
    readonly control: Control<ProfileForm>;
    readonly debounceDelay: number;
    readonly hiddenSkillIds?: ReadonlySet<string>;
    readonly onSkillChange?: () => void;
};

type UseSkillSearchResult = {
    readonly displayedSkills: readonly SkillShort[] | undefined;
    readonly handleSkillSelect: (skillId: string) => void;
    readonly isLoading: boolean;
    readonly isSkillSelected: (skillId: string) => boolean;
    readonly searchTerm: string;
    readonly setSearchTerm: (value: string) => void;
};

const useSkillSearch = ({
    architectureId,
    control,
    debounceDelay,
    hiddenSkillIds,
    onSkillChange,
}: UseSkillSearchParams): UseSkillSearchResult => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounceValue(searchTerm, debounceDelay);
    const { applicationState } = useApplicationContext();
    const { data: skills, isLoading } = useSkillsSearch({
        acceptLanguage: applicationState.language.code,
        searchTerm: debouncedSearchTerm,
    });
    const { trackSearchExecuted } = useTrackSearch({ architectureId });
    const { showInfoSnackbar } = useSnackbars();

    const { append, fields } = useFieldArray({
        control,
        keyName: '_id',
        name: 'skills',
    });

    const fixedSkills = useRef(fields ?? []);

    const { data: relatedSkills, isLoading: isRelatedSkillsLoading } = useRelatedSkills({
        acceptLanguage: applicationState.language.code,
        skills: fixedSkills.current,
    });

    const foundSkills = searchTerm.length > 0 ? skills : relatedSkills;
    const displayedSkills =
        hiddenSkillIds === undefined || hiddenSkillIds.size === 0
            ? foundSkills
            : foundSkills?.filter(({ id }) => !hiddenSkillIds.has(id));

    useEffect(() => {
        if (isLoading || !is.nonEmptyString(debouncedSearchTerm) || is.nullOrUndefined(skills)) {
            return;
        }

        trackSearchExecuted({
            query: debouncedSearchTerm,
            resultCount: skills.length,
            scope: 'skills',
        });
    }, [isLoading, debouncedSearchTerm, skills, trackSearchExecuted]);

    const handleSkillSelect = (skillId: string): void => {
        const skill = displayedSkills?.find((s) => s.id === skillId);
        if (is.nullOrUndefined(skill)) {
            return;
        }

        const alreadyExists = fields.some((field) => field.id === skill.id);
        if (!alreadyExists) {
            append(skill);
            onSkillChange?.();
            showInfoSnackbar('pages.job-architecture-v2.job-profile-skill-added-toast');
        }

        setSearchTerm('');
    };

    const isSkillSelected = (skillId: string): boolean => {
        return fields.some((field) => field.id === skillId);
    };

    return {
        displayedSkills,
        handleSkillSelect,
        isLoading: isLoading || isRelatedSkillsLoading,
        isSkillSelected,
        searchTerm,
        setSearchTerm,
    };
};

type SkillSearchContentProps = {
    readonly architectureId?: string;
    readonly control: Control<ProfileForm>;
    readonly debounceDelay?: number;
    readonly hiddenSkillIds?: ReadonlySet<string>;
    readonly onSkillChange?: () => void;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const findSearchTermMatches = (input: string, searchTerm: string): { val: string; match: boolean }[] => {
    const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi');
    let lastIndex = 0;
    const parts: { val: string; match: boolean }[] = [];
    for (const match of input.matchAll(regex)) {
        const start = match.index;
        const end = match.index + searchTerm.length;

        if (start > lastIndex) {
            parts.push({ val: input.slice(lastIndex, start), match: false });
        }
        parts.push({ val: input.slice(start, end), match: true });

        lastIndex = end;
    }

    if (lastIndex < input.length) parts.push({ val: input.slice(lastIndex), match: false });

    return parts;
};

const HighlightedText: FunctionComponent<{ text: string; searchTerm: string }> = ({ text, searchTerm }) => {
    if (searchTerm.length === 0) {
        return <span>{text}</span>;
    }

    const matches = findSearchTermMatches(text, searchTerm);

    return (
        <div>
            {matches.map((match) => (
                <span key={match.val} className={match.match ? 'font-bold' : 'color-primary'}>
                    {match.val}
                </span>
            ))}
        </div>
    );
};

type SkillSearchContentBaseProps = {
    readonly displayedSkills: readonly SkillShort[] | undefined;
    readonly groupLabel?: string;
    readonly inputId?: string;
    readonly isLoading: boolean;
    readonly isSkillSelected: (skillId: string) => boolean;
    readonly languageCode?: string;
    readonly onSkillSelect: (skillId: string) => void;
    readonly placeholder?: string;
    readonly searchTerm: string;
    readonly setSearchTerm: (value: string) => void;
};

const SKELETON_ITEM_COUNT = 4;

export const SkillSearchContentBase: FunctionComponent<SkillSearchContentBaseProps> = ({
    displayedSkills,
    groupLabel,
    inputId = 'skill-search-input',
    isLoading,
    isSkillSelected,
    languageCode,
    onSkillSelect,
    placeholder,
    searchTerm,
    setSearchTerm,
}) => {
    const translate = useTranslate();
    const alreadyAddedTooltip = translate('pages.job-architecture-v2.job-profile-skill-already-added-tooltip');
    const inputRef = useRef<HTMLInputElement>(null);
    const showSkeletons = isLoading && (displayedSkills === undefined || displayedSkills.length === 0);
    const showGroupLabel = is.nonEmptyString(groupLabel) && !is.emptyArray(displayedSkills);

    useEffect(() => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
    }, []);

    return (
        <div className="flex flex-col gap-s min-w-[240px] overflow-hidden">
            <div className="flex items-center bg-transparent border-b border-gray-400 gap-s p-s relative">
                <label htmlFor={inputId} className="flex items-center text-gray-700 opacity-50">
                    <IconSearch size={12} />
                </label>
                <Input
                    ref={inputRef}
                    className="text-gray-700 flex-1 text-body-s font-normal pl-s tracking-tight"
                    id={inputId}
                    variant="ghost"
                    placeholder={
                        placeholder ?? translate('pages.job-architecture-v2.job-profile-skill-search-placeholder')
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDownCapture={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                />
                {isLoading && <Spinner className="absolute right-m" />}
            </div>
            {showGroupLabel && <DropdownMenuLabel>{groupLabel}</DropdownMenuLabel>}
            <ScrollArea orientation="vertical">
                <div className="flex flex-auto flex-col gap-1 h-[25vh]">
                    {showSkeletons &&
                        Array.from({ length: SKELETON_ITEM_COUNT }).map((_, index) => (
                            <SkeletonBase
                                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders are static.
                                key={index}
                                style={{ height: '24px', margin: '4px 8px', width: '60%' }}
                            />
                        ))}
                    {!showSkeletons && is.emptyArray(displayedSkills) && (
                        <div className="text-gray-500 text-sm p-m text-center">
                            {translate('validation.empty-search-results')}
                        </div>
                    )}
                    {!showSkeletons &&
                        displayedSkills?.map((skill) => {
                            const title = getSkillTitle(skill, languageCode);

                            if (isSkillSelected(skill.id)) {
                                return (
                                    <DropdownMenuItem
                                        key={skill.id}
                                        className="flex-1 bg-status-success-surface"
                                        disabled={true}
                                        tooltipContent={alreadyAddedTooltip}
                                    >
                                        <span className="min-w-0 truncate text-text-form">
                                            <HighlightedText text={title} searchTerm={searchTerm} />
                                        </span>
                                        <span className="ml-auto flex shrink-0 [&_svg]:!text-status-success-text">
                                            <IconCircleCheckLight size={14} />
                                        </span>
                                    </DropdownMenuItem>
                                );
                            }

                            return (
                                <DropdownMenuItem key={skill.id} onSelect={() => onSkillSelect(skill.id)}>
                                    <span className="min-w-0 truncate">
                                        <HighlightedText text={title} searchTerm={searchTerm} />
                                    </span>
                                    <SkillNotSyncedWarning
                                        className="ml-auto !text-warning-500"
                                        isSynced={skill.isSynced}
                                    />
                                </DropdownMenuItem>
                            );
                        })}
                </div>
            </ScrollArea>
        </div>
    );
};

export const SkillSearchContent: FunctionComponent<SkillSearchContentProps> = ({
    architectureId,
    control,
    debounceDelay = 500,
    hiddenSkillIds,
    onSkillChange,
}) => {
    const translate = useTranslate();
    const { displayedSkills, handleSkillSelect, isLoading, isSkillSelected, searchTerm, setSearchTerm } =
        useSkillSearch({
            architectureId,
            control,
            debounceDelay,
            hiddenSkillIds,
            onSkillChange,
        });
    const groupLabel = searchTerm.length === 0 ? translate('pages.job-architecture-v2.suggested') : undefined;

    return (
        <SkillSearchContentBase
            displayedSkills={displayedSkills}
            groupLabel={groupLabel}
            isLoading={isLoading}
            isSkillSelected={isSkillSelected}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSkillSelect={handleSkillSelect}
        />
    );
};
